/**
 * Saved Views Data Model
 * 
 * Types and utilities for the Fokus Views feature.
 * Views are dynamic queries that can span multiple campaigns,
 * apply filters, and optionally flatten nested campaigns.
 */

/**
 * @typedef {'flow' | 'kanban' | 'todo'} ViewMode
 * The display mode for rendering a saved view
 */

/**
 * @typedef {'active' | 'archived' | 'deleted'} PinnedTaskStatus
 * Status of a pinned task for graceful handling of removed tasks
 */

/**
 * @typedef {Object} PinnedTask
 * A specific task pinned to a view (always included regardless of filters)
 * @property {string} taskId - The task's unique ID
 * @property {string} campaignId - The campaign containing this task
 * @property {string} lastKnownName - Cached name for display if task is deleted
 * @property {PinnedTaskStatus} status - Current status of the pinned task
 */

/**
 * @typedef {Object} ExplodeSettings
 * Configuration for flattening nested campaigns
 * @property {boolean} enabled - Whether to flatten nested campaigns
 * @property {number | 'all'} depth - How many levels to flatten (1, 2, 3, or 'all')
 */

/**
 * @typedef {Object} ViewFilters
 * Filter criteria for which tasks to include
 * @property {string[]} [statusCategories] - Filter by status category ('not_started', 'in_progress', 'blocked', 'completed')
 * @property {string[]} [statusIds] - Filter by specific status IDs
 * @property {string} [priority] - Filter by priority ('high', 'medium', 'low')
 * @property {string[]} [categoryIds] - Filter by category/tag IDs
 * @property {boolean} [hasDeliverables] - Filter to tasks with deliverables
 * @property {boolean} [hasDueDate] - Filter to tasks with due dates
 * @property {boolean} [isOverdue] - Filter to overdue tasks
 */

/**
 * @typedef {Object} ViewQuery
 * The query definition for a saved view
 * @property {'all' | string[]} scope - 'all' campaigns or array of specific campaign IDs
 * @property {ExplodeSettings} explode - Settings for flattening nested campaigns
 * @property {ViewFilters} filters - Filter criteria
 * @property {PinnedTask[]} [pinnedTasks] - Specific tasks to always include
 */

/**
 * @typedef {Object} SavedView
 * A saved view configuration
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {string} [icon] - Emoji icon (optional)
 * @property {ViewQuery} query - The view query definition
 * @property {ViewMode} viewMode - How to render the view
 * @property {boolean} isBuiltIn - Legacy flag; new views are never built-in
 * @property {string} [createdAt] - ISO timestamp of creation
 * @property {string} [updatedAt] - ISO timestamp of last update
 */

/** Seeded views that are no longer offered. */
export const RETIRED_VIEW_IDS = new Set(['view-all-actionable', 'view-all-campaigns']);

export function isRetiredViewId(viewId) {
  return RETIRED_VIEW_IDS.has(viewId);
}

/**
 * Creates a new empty view with default values
 * @param {Partial<SavedView>} [overrides] - Optional values to override defaults
 * @returns {SavedView}
 */
export function createEmptyView(overrides = {}) {
  const now = new Date().toISOString();
  return {
    id: `view-${Date.now()}`,
    name: 'New View',
    icon: '👁️',
    query: {
      scope: 'all',
      explode: { enabled: false, depth: 1 },
      filters: {},
      pinnedTasks: [],
    },
    viewMode: 'flow',
    isBuiltIn: false,
    createdAt: now,
    updatedAt: now,
    ...overrides,
  };
}

/**
 * Creates a view from the current app state (for "Capture View" feature)
 * @param {Object} params
 * @param {string} [params.currentCampaignId] - Currently viewed campaign
 * @param {Object} [params.activeFilters] - Currently applied filters
 * @param {string[]} [params.focusedTaskIds] - Currently focused task IDs
 * @param {ViewMode} [params.currentViewMode] - Current view mode
 * @returns {SavedView}
 */
export function captureCurrentState({
  currentCampaignId,
  activeFilters = {},
  focusedTaskIds = [],
  currentViewMode = 'flow',
} = {}) {
  const now = new Date().toISOString();
  
  return {
    id: `view-${Date.now()}`,
    name: 'Captured View',
    icon: '📷',
    query: {
      scope: currentCampaignId ? [currentCampaignId] : 'all',
      explode: { enabled: false, depth: 1 },
      filters: activeFilters,
      pinnedTasks: focusedTaskIds.map(taskId => ({
        taskId,
        campaignId: currentCampaignId || '',
        lastKnownName: '', // Will be populated when saving
        status: 'active',
      })),
    },
    viewMode: currentViewMode,
    isBuiltIn: false,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Validates a SavedView object
 * @param {SavedView} view
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateView(view) {
  const errors = [];
  
  if (!view.id) errors.push('View must have an ID');
  if (!view.name || view.name.trim() === '') errors.push('View must have a name');
  if (!view.query) errors.push('View must have a query');
  if (!['flow', 'kanban', 'todo'].includes(view.viewMode)) {
    errors.push('View mode must be flow, kanban, or todo');
  }
  
  if (view.query) {
    if (view.query.scope !== 'all' && !Array.isArray(view.query.scope)) {
      errors.push('Query scope must be "all" or an array of campaign IDs');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Status category options for the filter UI
 */
export const STATUS_CATEGORY_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: '#6b7280' },
  { value: 'in_progress', label: 'In Progress', color: '#f59e0b' },
  { value: 'blocked', label: 'Blocked', color: '#ef4444' },
  { value: 'completed', label: 'Completed', color: '#10b981' },
];

/**
 * Priority options for the filter UI
 */
export const PRIORITY_OPTIONS = [
  { value: 'high', label: 'High', color: '#ef4444' },
  { value: 'medium', label: 'Medium', color: '#f59e0b' },
  { value: 'low', label: 'Low', color: '#22c55e' },
];

/**
 * Explode depth options for the UI
 */
export const EXPLODE_DEPTH_OPTIONS = [
  { value: 1, label: '1 level' },
  { value: 2, label: '2 levels' },
  { value: 3, label: '3 levels' },
  { value: 'all', label: 'All levels' },
];

